/**
 * Action Type Resolver Utility
 * Centralizes the mapping between URL action params, stage types, and step types
 */

export interface ResolvedAction {
  actionType: string;
  stepType: string;
}

/**
 * Map from URL action parameter to internal step type
 */
const ACTION_TO_STEP_TYPE: Record<string, string> = {
  'start_ai_interview': 'aiInterview',
  'start_quiz': 'multipleChoice',
  'book_interview': 'humanInterview',
  'start_assignment': 'customAssignment',
  'view_application': 'view',
  'view_stage': 'view',
  'start_stage': 'auto',
  'start': 'auto',
  'view': 'view'
};

/**
 * Map from stage type to action and step type
 */
const STAGE_TYPE_TO_ACTION: Record<string, ResolvedAction> = {
  'ai_interview': { actionType: 'start_ai_interview', stepType: 'aiInterview' },
  'human_interview': { actionType: 'book_interview', stepType: 'humanInterview' },
  'multiple_choice': { actionType: 'start_quiz', stepType: 'multipleChoice' },
  'quiz': { actionType: 'start_quiz', stepType: 'multipleChoice' },
  'custom_assignment': { actionType: 'start_assignment', stepType: 'customAssignment' },
  'assignment': { actionType: 'start_assignment', stepType: 'customAssignment' },
  'resume_review': { actionType: 'view_stage', stepType: 'view' },
  'final_verdict': { actionType: 'view_stage', stepType: 'view' }
};

/**
 * Resolve action type from URL params
 * 
 * @param params - Object containing action and/or stageType from URL
 * @returns Resolved action and step types
 */
export function resolveActionFromParams(params: {
  action?: string | null;
  stageType?: string | null;
}): ResolvedAction {
  const { action, stageType } = params;
  
  // If explicit action provided, use it
  if (action) {
    const stepType = ACTION_TO_STEP_TYPE[action] || 'view';
    
    // If stepType is 'auto', try to infer from stageType
    if (stepType === 'auto' && stageType) {
      const resolved = STAGE_TYPE_TO_ACTION[stageType];
      if (resolved) {
        return resolved;
      }
    }
    
    return { actionType: action, stepType };
  }
  
  // Infer from stage type
  if (stageType) {
    const resolved = STAGE_TYPE_TO_ACTION[stageType];
    if (resolved) {
      return resolved;
    }
  }
  
  return { actionType: 'view', stepType: 'view' };
}

/**
 * Get the action type from a stage type
 */
export function getActionFromStageType(stageType: string): string {
  const resolved = STAGE_TYPE_TO_ACTION[stageType];
  return resolved?.actionType || 'view_stage';
}

/**
 * Get the step type from an action
 */
export function getStepTypeFromAction(action: string): string {
  return ACTION_TO_STEP_TYPE[action] || 'view';
}
